import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { isCatalyticConverter } from '@rematter/modules/trade/utils/catalyticConverter';
import { cn, Grid } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $autoFocusRowId,
  $openCatalyticModal,
  $rowFocusState,
  $setRowFocus,
} from '../../../_state/loadStaged.state';

import { LoadStagedFieldProps } from './_types';
import {
  CameraButtonField,
  DeleteButtonField,
  EditItemButtonField,
  GrossWeightField,
  InventoryItemCatalyticField,
  InventoryItemSelectField,
  InventoryTagItemSelectField,
  ItemSizeField,
  NetWeightField,
  TareWeightField,
} from './fields';

import { useInventoryItem } from '~/modules/trade/hooks/useInventoryItem';
import { LoadFormLineItem, WeightUnitType } from '~/modules/trade/types/load';
import { useUIPreferences } from '~/shared/hooks';

const InventoryField: React.FC<LoadStagedFieldProps> = (props) => {
  const isCatalyticConverterItem = isCatalyticConverter(
    props.inventoryItem?.category?.name,
  );

  if (props.lineItem.type !== 'Raw') {
    return <InventoryTagItemSelectField {...props} />;
  }
  if (isCatalyticConverterItem) {
    return <InventoryItemCatalyticField {...props} />;
  }
  return <InventoryItemSelectField {...props} className="@InventoryField" />;
};

type LoadStagedItemLineProps = {
  lineItem: LoadFormLineItem;
  className?: string;
  disabled?: boolean;
  weightUnit?: WeightUnitType;
  facilityDefaultUnit?: Unit;
  autoFocus?: boolean;
  onUpdateItem: (id: string, updates: Partial<LoadFormLineItem>) => void;
  onDeleteItem: (id: string) => void;
};

export const LoadStagedItemLine: React.FC<LoadStagedItemLineProps> = ({
  lineItem,
  disabled,
  onUpdateItem,
  onDeleteItem,
  className,
  autoFocus = false,
  /*
   *  ALWAYS use SCALE unit and pounds as the default unit for staged line items - should reflect in Footer
   */
  weightUnit = 'scale',
  facilityDefaultUnit = enumUnit.Pounds,
}) => {
  const grossWeightRef = React.useRef<HTMLDivElement>(null);
  const inventorySelectRef = React.useRef<HTMLDivElement>(null);

  const inventoryItem = useInventoryItem(lineItem.itemId);

  const rowFocusState = useAtomValue($rowFocusState);
  const autoFocusRowId = useAtomValue($autoFocusRowId);
  const setRowFocusState = useSetAtom($setRowFocus);
  const setAutoFocusRowId = useSetAtom($autoFocusRowId);
  const openCatalyticModal = useSetAtom($openCatalyticModal);

  const isCatalyticConverterItem = isCatalyticConverter(
    inventoryItem?.category?.name,
  );

  const { touchModeEnabled } = useUIPreferences();

  const isFocused = rowFocusState.get(lineItem.id) === true;

  React.useEffect(() => {
    const shouldAutoFocus = autoFocus || autoFocusRowId === lineItem.id;
    if (!shouldAutoFocus) return;

    // Determine target ref based on source:
    // - From RequestedMaterialsList: never focus inventory select, only focus gross weight for Raw items
    // - From autoFocusRowId: touch mode focuses gross weight, non-touch focuses inventory select
    const getTargetRef = () => {
      if (autoFocus) {
        return lineItem.type === 'Raw' ? grossWeightRef : null;
      }
      return touchModeEnabled ? grossWeightRef : inventorySelectRef;
    };

    const inputElement = getTargetRef()?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }

    if (autoFocusRowId === lineItem.id) {
      setTimeout(() => {
        setAutoFocusRowId(null);
      }, 500);
    }
  }, [
    autoFocus,
    autoFocusRowId,
    lineItem.id,
    lineItem.type,
    setAutoFocusRowId,
    touchModeEnabled,
  ]);

  const handleFocus = React.useCallback(() => {
    setRowFocusState({ rowId: lineItem.id, isFocused: true });
  }, [lineItem.id, setRowFocusState]);

  const handleBlur = React.useCallback(() => {
    setRowFocusState({ rowId: lineItem.id, isFocused: false });
  }, [lineItem.id, setRowFocusState]);

  const handleClickCatalyticConverter = () => {
    openCatalyticModal({
      mode: 'change',
      selectedItemId: lineItem.itemId ?? null,
      lineItemId: lineItem.id,
    });
    setRowFocusState({ rowId: lineItem.id, isFocused: false });
  };

  const fieldProps = {
    lineItem,
    inventoryItem,
    disabled,
    onUpdateItem,
    onDeleteItem,
    weightUnit,
    facilityDefaultUnit,
    touchModeEnabled,
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  return (
    <Grid
      className={cn(
        'w-full gap-3 p-2',
        'grid-cols-[2fr_1fr_1fr_1fr_var(--size-input)_var(--size-input)]',
        'relative border-2 border-transparent after:absolute after:right-0 after:bottom-0 after:left-0 after:h-px after:bg-neutral/20 last:after:hidden',
        isFocused && 'rounded-lg border-primary ring-2 ring-primary/10',
        className,
      )}
    >
      <div ref={inventorySelectRef}>
        <InventoryField
          {...fieldProps}
          onClick={handleClickCatalyticConverter}
        />
      </div>

      <div ref={grossWeightRef}>
        <GrossWeightField {...fieldProps} />
      </div>

      {isCatalyticConverterItem ? (
        <ItemSizeField {...fieldProps} />
      ) : (
        <TareWeightField {...fieldProps} />
      )}

      <NetWeightField {...fieldProps} />

      {isCatalyticConverterItem ? (
        <EditItemButtonField {...fieldProps} />
      ) : (
        <CameraButtonField {...fieldProps} />
      )}
      <DeleteButtonField {...fieldProps} />
    </Grid>
  );
};
